/**
 * Evaluation Store (Zustand)
 *
 * Manages state for the evaluation UI:
 * - Form inputs (dataset name, negotiation ID)
 * - Run status and polling
 * - Results display
 * - Human feedback submission
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  EvaluationAPI,
  EvaluationRunStatus,
  EvaluationResult,
} from '../api/evaluation';
import { useToastStore } from './toastStore';

// ==================== Types ====================

interface EvaluationState {
  // Form state
  datasetName: string;
  negotiationId: number | null;
  maxItems: number | null;

  // Run state
  currentRunId: string | null;
  runStatus: EvaluationRunStatus | null;
  results: EvaluationResult[];

  // UI state
  loading: boolean;
  polling: boolean;
  pollingInterval: number | null;

  // Actions
  setDatasetName: (name: string) => void;
  setNegotiationId: (id: number | null) => void;
  setMaxItems: (max: number | null) => void;
  startRun: () => Promise<void>;
  pollRunStatus: () => Promise<void>;
  startPolling: () => void;
  stopPolling: () => void;
  fetchResults: () => Promise<void>;
  submitFeedback: (
    traceId: string,
    thumbsUp: boolean,
    notes?: string
  ) => Promise<void>;
  reset: () => void;
}

// ==================== Store ====================

export const useEvaluationStore = create<EvaluationState>()(
  devtools(
    (set, get) => ({
      // Initial state
      datasetName: '',
      negotiationId: null,
      maxItems: null, // Default to null (all items)
      currentRunId: null,
      runStatus: null,
      results: [],
      loading: false,
      polling: false,
      pollingInterval: null,

      // Setters
      setDatasetName: (name: string) => set({ datasetName: name }),
      setNegotiationId: (id: number | null) => set({ negotiationId: id }),
      setMaxItems: (max: number | null) => set({ maxItems: max }),

      // Start evaluation run
      startRun: async () => {
        const { datasetName, negotiationId, maxItems } = get();

        // Validation
        if (!datasetName || !negotiationId) {
          useToastStore.getState().error('Validation Error', 'Please enter dataset name and negotiation ID');
          return;
        }

        set({ loading: true });

        try {
          const result = await EvaluationAPI.startRun({
            dataset_name: datasetName,
            negotiation_id: negotiationId,
            max_items: maxItems || undefined,
          });

          set({
            currentRunId: result.run_id,
            loading: false,
          });

          useToastStore.getState().success('Evaluation Started', 'Evaluation is running in the background');

          // Start polling
          get().startPolling();
        } catch (error: any) {
          set({ loading: false });
          useToastStore.getState().error('Failed to Start', error.message);
        }
      },

      // Poll run status
      pollRunStatus: async () => {
        const { currentRunId, polling } = get();

        if (!currentRunId || !polling) return;

        try {
          const status = await EvaluationAPI.getRunStatus(currentRunId);
          set({ runStatus: status });

          // Check if completed
          if (status.status === 'COMPLETED') {
            get().stopPolling();
            useToastStore.getState().success('Evaluation Completed', `Processed ${status.total_items} items successfully`);

            // Fetch results
            await get().fetchResults();
          } else if (status.status === 'FAILED') {
            get().stopPolling();
            useToastStore.getState().error(
              'Evaluation Failed',
              status.error_message || 'Unknown error'
            );
          }
        } catch (error: any) {
          console.error('Failed to poll status:', error);
          // Don't stop polling on error, just log it
        }
      },

      // Start polling (every 2 seconds)
      startPolling: () => {
        set({ polling: true });

        // Poll immediately
        get().pollRunStatus();

        // Then poll every 2 seconds
        const interval = setInterval(() => {
          get().pollRunStatus();
        }, 2000);

        set({ pollingInterval: interval });
      },

      // Stop polling
      stopPolling: () => {
        const { pollingInterval } = get();

        if (pollingInterval) {
          clearInterval(pollingInterval);
        }

        set({ polling: false, pollingInterval: null });
      },

      // Fetch results from backend
      fetchResults: async () => {
        const { currentRunId } = get();

        if (!currentRunId) {
          console.error('[EvalStore] fetchResults: No currentRunId');
          return;
        }

        console.log('[EvalStore] Fetching results for run:', currentRunId);
        set({ loading: true });

        try {
          const results = await EvaluationAPI.getRunResults(currentRunId);
          console.log('[EvalStore] Received results:', results);
          console.log('[EvalStore] Results count:', results.length);
          set({ results, loading: false });

          if (results.length === 0) {
            useToastStore.getState().error('No Results', 'The evaluation completed but returned no results');
          }
        } catch (error: any) {
          console.error('[EvalStore] Failed to fetch results:', error);
          set({ loading: false });
          useToastStore.getState().error('Failed to Fetch Results', error.message);
        }
      },

      // Submit human feedback
      submitFeedback: async (traceId: string, thumbsUp: boolean, notes?: string) => {
        try {
          await EvaluationAPI.submitFeedback(traceId, {
            thumbs_up: thumbsUp,
            notes: notes || '',
          });

          // Update local state (optimistic update)
          set((state) => ({
            results: state.results.map((r) =>
              r.trace_id === traceId
                ? { ...r, human_thumbs_up: thumbsUp, human_notes: notes }
                : r
            ),
          }));

          useToastStore.getState().success('Feedback Submitted', 'Thank you for your feedback!');
        } catch (error: any) {
          useToastStore.getState().error('Failed to Submit Feedback', error.message);
        }
      },

      // Reset state
      reset: () => {
        get().stopPolling();
        set({
          datasetName: '',
          negotiationId: null,
          maxItems: null,
          currentRunId: null,
          runStatus: null,
          results: [],
          loading: false,
          polling: false,
          pollingInterval: null,
        });
      },
    }),
    { name: 'EvaluationStore' }
  )
);
